import {
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    signal,
    ViewEncapsulation
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GeneratedFormGroup, NgxFormGeneratorProvider } from "@recursyve/ngx-form-generator";
import { map, Observable, of } from "rxjs";
import { RegisterOwnersDto } from "../../../../api/nerri/owners/dto/register-owners.dto";
import { RegisterOwnerForm } from "./register-owner.form";

@Component({
    selector: "nerri-register-owner-form",
    standalone: false,
    templateUrl: "./register-owner-form.template.html",
    styleUrls: ["./register-owner-form.style.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: NgxFormGeneratorProvider.forFeature([RegisterOwnerForm])
})
export class RegisterOwnerFormComponent implements OnInit {
    @Input()
    public saving = false;

    @Output()
    public readonly register = new EventEmitter<Omit<RegisterOwnersDto, "uuid">>();

    protected showPassword = signal(false);
    protected showPasswordInputType = computed(() => (this.showPassword() ? "text" : "password"));

    protected showConfirmPassword = signal(false);
    protected showConfirmPasswordInputType = computed(() => (this.showConfirmPassword() ? "text" : "password"));

    protected has1UppercaseLetter$: Observable<boolean | null> = of(null);
    protected has1LowercaseLetter$: Observable<boolean | null> = of(null);
    protected has1Number$: Observable<boolean | null> = of(null);
    protected has8Characters$: Observable<boolean | null> = of(null);
    protected has1Symbol$: Observable<boolean | null> = of(null);

    private readonly destroyRef = inject(DestroyRef);

    constructor(protected formGroup: GeneratedFormGroup<RegisterOwnerForm>) {}

    public ngOnInit(): void {
        const passwordControl = this.formGroup.get("password");
        if (!passwordControl) {
            return;
        }

        this.has1UppercaseLetter$ = passwordControl.statusChanges.pipe(
            map(() => this.validatePasswordRule("must_have_1_uppercase_letter"))
        );

        this.has1LowercaseLetter$ = passwordControl.statusChanges.pipe(
            map(() => this.validatePasswordRule("must_have_1_lowercase_letter"))
        );

        this.has1Number$ = passwordControl.statusChanges.pipe(
            map(() => this.validatePasswordRule("must_have_1_number"))
        );

        this.has8Characters$ = passwordControl.statusChanges.pipe(map(() => this.validatePasswordRule("minlength")));

        this.has1Symbol$ = passwordControl.statusChanges.pipe(
            map(() => this.validatePasswordRule("must_have_1_symbol"))
        );

        passwordControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.formGroup.get("confirmPassword")?.updateValueAndValidity());
    }

    protected clickSubmit(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        this.register.emit(this.formGroup.getRawValue().toDto());
    }

    private validatePasswordRule(rule: string): boolean | null {
        const passwordControl = this.formGroup.get("password");

        if (!passwordControl?.value) {
            return null;
        }

        return !passwordControl.hasError(rule);
    }
}
